const MainSearch = class extends HTMLElement {
  constructor() {
    super();

    this.initPopularProducts();

    // Clicking close button
    this.querySelectorAll('.main-search__close').forEach((el) => {
      el.addEventListener('click', (evt) => {
        evt.preventDefault();
        document.body.classList.remove('show-search');
      });
    });

    // Pressing escape key
    this.querySelector('.main-search__input').addEventListener('keyup', (evt) => {
      if (evt.key === 'Escape') {
        this.querySelector('.main-search__close').dispatchEvent(new Event('click'));
      }
    });

    // Search as you type
    if (this.dataset.quickSearch === 'true') {
      this.initQuickSearch();
    }
  }

  initPopularProducts() {
    if (!this.querySelector('[data-popular-products]')) return;

    const pageHeader = this.closest('page-header');
    pageHeader?.querySelectorAll('.show-search-link').forEach((link) => {
      // Start before click where possible, while keeping products out of the initial HTML.
      link.addEventListener('pointerenter', () => this.loadPopularProducts());
      link.addEventListener('focus', () => this.loadPopularProducts());
      link.addEventListener('pointerdown', () => this.loadPopularProducts());
      link.addEventListener('click', () => {
        this.loadPopularProducts({ showLoading: true });
        requestAnimationFrame(() => this.refreshPopularProductsCarousel());
      });
    });

    // After the first real interaction, preload during browser idle time.
    const queueIdlePreload = () => this.queuePopularProductsIdlePreload();
    document.addEventListener('pointerdown', queueIdlePreload, { once: true, passive: true });
    document.addEventListener('keydown', queueIdlePreload, { once: true });
    window.addEventListener('scroll', queueIdlePreload, { once: true, passive: true });
  }

  queuePopularProductsIdlePreload() {
    if (this.popularProductsIdlePreloadQueued) return;
    this.popularProductsIdlePreloadQueued = true;

    if ('requestIdleCallback' in window) {
      window.requestIdleCallback(() => this.loadPopularProducts(), { timeout: 1500 });
    } else {
      setTimeout(() => this.loadPopularProducts(), 500);
    }
  }

  getPopularProductsCacheKey(url) {
    const language = document.documentElement.lang || '';
    const country = window.Shopify?.country || '';
    const currency = window.Shopify?.currency?.active || '';
    return `popular-products-v1:${language}:${country}:${currency}:${url}`;
  }

  getCachedPopularProducts(url) {
    try {
      return window.sessionStorage.getItem(this.getPopularProductsCacheKey(url));
    } catch (error) {
      return null;
    }
  }

  cachePopularProducts(url, markup) {
    try {
      window.sessionStorage.setItem(this.getPopularProductsCacheKey(url), markup);
    } catch (error) {
      // Storage may be unavailable in private browsing; loading still works normally.
    }
  }

  renderPopularProducts(markup, container, grid) {
    const template = document.createElement('template');
    template.innerHTML = markup;

    if (!template.content.querySelector('.slider__item')) return false;

    grid.innerHTML = markup;
    container.dataset.popularProductsLoaded = 'true';

    // Warm the images that are visible first when the drawer opens.
    Array.from(grid.querySelectorAll('.slider__item')).slice(0, 5).forEach((item) => {
      const image = item.querySelector('img');
      if (image) image.loading = 'eager';
    });

    if (document.body.classList.contains('show-search')) {
      this.refreshPopularProductsCarousel();
    }

    return true;
  }

  refreshPopularProductsCarousel() {
    const carousel = this.querySelector('[data-popular-products] carousel-slider');
    if (!carousel) return;

    customElements.whenDefined('carousel-slider').then(() => {
      requestAnimationFrame(() => carousel.refresh());
    });
  }

  loadPopularProducts({ showLoading = false } = {}) {
    const container = this.querySelector('[data-popular-products]');
    if (!container) return;

    const url = container.dataset.popularProductsUrl;
    const grid = container.querySelector('.slider__grid');
    const loading = container.querySelector('[data-popular-products-loading]');
    const fallback = container.querySelector('[data-popular-products-fallback]');
    if (!url || !grid) return;

    if (container.dataset.popularProductsLoaded === 'true') {
      if (showLoading) this.refreshPopularProductsCarousel();
      return;
    }

    if (showLoading && loading) loading.hidden = false;
    if (showLoading && fallback) fallback.hidden = true;
    if (this.popularProductsRequest) return;

    const cachedMarkup = this.getCachedPopularProducts(url);
    if (cachedMarkup && this.renderPopularProducts(cachedMarkup, container, grid)) {
      if (loading) loading.hidden = true;
      return;
    }

    container.setAttribute('aria-busy', 'true');

    this.popularProductsRequest = fetch(url, { method: 'get' })
      .then((response) => {
        if (!response.ok) {
          throw new Error(`HTTP error! Status: ${response.status}`);
        }
        return response.text();
      })
      .then((response) => {
        const template = document.createElement('template');
        template.innerHTML = response;
        const section = template.content.querySelector('#shopify-section-search-popular-products');

        if (!section || !section.querySelector('.slider__item')) {
          throw new Error('Popular products section returned no products');
        }

        const markup = section.innerHTML;
        this.cachePopularProducts(url, markup);
        this.renderPopularProducts(markup, container, grid);
      })
      .catch((error) => {
        console.warn('Unable to load popular products', error);
        if (fallback) fallback.hidden = false;
      })
      .finally(() => {
        container.removeAttribute('aria-busy');
        if (loading) loading.hidden = true;
        this.popularProductsRequest = null;
      });
  }

  initQuickSearch() {
    const searchInput = this.querySelector('.main-search__input');
    const searchTimeoutThrottle = 500;
    const resultLimit = 8;
    const includeMeta = this.dataset.quickSearchMeta === 'true';
    let searchTimeoutID = -1;
    let searchAbortController = null;

    const handleInputChange = () => {
      const resultsBox = this.querySelector('.main-search__results');
      const valueToSearch = searchInput.value;

      // Only search if search string longer than 2, and it has changed
      if (valueToSearch.length && valueToSearch !== this.oldSearchValue) {
        // Save previous value
        this.oldSearchValue = valueToSearch;

        // Kill outstanding ajax request
        if (searchAbortController !== null) {
          searchAbortController.abort('Existing request not needed');
          searchAbortController = null;
        }

        // Kill previous search
        clearTimeout(searchTimeoutID);

        // Create URL for full search results
        const form = searchInput.closest('form');
        const linkURL = new URL(form.action);
        const formParams = new URLSearchParams(new FormData(form));
        formParams.forEach((value, key) => { linkURL.searchParams.set(key, value); });

        // Show loading
        this.classList.remove('main-search--has-results', 'main-search--results-on-multiple-lines', 'main-search--no-results');
        this.classList.add('main-search--loading');
        if (!this.querySelector('.main-search__results-spinner')) {
          resultsBox.innerHTML = '<div class="main-search__results-spinner"><div class="loading-spinner"></div></div>';
        }

        // Do next search (in X milliseconds)
        searchTimeoutID = setTimeout(() => {
          searchAbortController = new AbortController();
          let ajaxUrl;

          if (theme.Shopify.features.predictiveSearch) {
            // use the API
            ajaxUrl = new URL(theme.routes.base + theme.routes.predictiveSearch);
            ajaxUrl.searchParams.set('q', valueToSearch);
            ajaxUrl.searchParams.set('section_id', 'predictive-search');
            ajaxUrl.searchParams.set('resources[limit]', resultLimit);
            ajaxUrl.searchParams.set('resources[options][fields]', includeMeta ? 'title,product_type,variants.title,vendor,tag,variants.sku' : 'title,product_type,variants.title,vendor');
          } else {
            // use the theme template fallback
            ajaxUrl = new URL(linkURL);
            ajaxUrl.searchParams.set('section_id', 'main-search');
          }

          fetch(ajaxUrl, { method: 'get', signal: searchAbortController.signal })
            .then((response) => {
              if (!response.ok) {
                throw new Error(`HTTP error! Status: ${response.status}`);
              }
              return response.text();
            })
            .then((response) => {
              const template = document.createElement('template');
              template.innerHTML = response;
              let resultsList = null;
              if (theme.Shopify.features.predictiveSearch) {
                resultsList = template.content.querySelector('.product-grid');
              } else {
                resultsList = template.content.querySelector('.section-search-template .product-grid');
              }

              // reset previous contents
              searchAbortController = null;
              this.classList.remove('main-search--has-results', 'main-search--results-on-multiple-lines', 'main-search--no-results');
              resultsBox.innerHTML = '';

              const resultsProducts = document.createElement('div');
              resultsProducts.className = 'main-search__results__products collection-listing';
              resultsProducts.innerHTML = '<div></div>';
              const resultsPages = document.createElement('div');
              resultsPages.className = 'main-search__results__pages';

              resultsProducts.firstElementChild.className = resultsList.className;

              // Convert standard blocks into quick search result format
              resultsList.querySelectorAll('.product-block:not(.collection-block):not(.page-block)').forEach((block, index) => {
                if (index <= resultLimit) {
                  if(block.dataset.productId == "9851438530841") return; // remove free product from search
                  block.classList.add('main-search-result');
                  block.querySelectorAll('.btn.quickbuy-toggle').forEach((el) => el.remove());
                  block.querySelectorAll('.quickbuy-toggle').forEach((el) => el.classList.remove('quickbuy-toggle'));
                  resultsProducts.firstElementChild.appendChild(block);
                }
              });

              resultsList.querySelectorAll('.product-block.page-block').forEach((block) => {
                const item = document.createElement('a');
                item.className = 'main-search-result main-search-result--page';
                item.href = block.querySelector('a').href;
                item.innerHTML = '<div class="main-search-result__text"></div>';
                item.firstElementChild.innerText = block.querySelector('.page-block__title').innerText;
                resultsPages.appendChild(item);
              });

              this.classList.remove('main-search--loading');

              const areProducts = !!resultsProducts.querySelector('.main-search-result');
              const arePages = !!resultsPages.querySelector('.main-search-result');
              if (areProducts || arePages) {
                // Numerous results
                this.classList.add('main-search--has-results');
                this.classList.toggle('main-search--results-on-multiple-lines', resultsProducts.querySelectorAll('.product-block').length > 4);
                if (areProducts) {
                  resultsBox.appendChild(resultsProducts);
                }
                if (arePages) {
                  const heading = document.createElement('h6');
                  heading.className = 'main-search-result__heading';
                  heading.innerHTML = theme.strings.generalSearchPages;
                  resultsPages.insertAdjacentElement('afterbegin', heading);
                  resultsBox.appendChild(resultsPages);
                }

                const allLink = document.createElement('a');
                allLink.className = 'main-search__results-all-link btn btn--secondary';
                allLink.href = linkURL;
                allLink.innerHTML = theme.strings.generalSearchViewAll;
                resultsBox.appendChild(allLink);
              } else {
                // No results - show nothing
                this.classList.add('main-search--no-results');
                const emptyMessage = document.createElement('div');
                emptyMessage.className = 'main-search__empty-message';
                emptyMessage.innerHTML = theme.strings.generalSearchNoResultsWithoutTerms;
                resultsBox.appendChild(emptyMessage);
              }
            })
            .catch((error) => {
              console.warn(error);
            });
        }, searchTimeoutThrottle);
      } else if (!valueToSearch.length) {
        // Abandon current search
        this.oldSearchValue = valueToSearch;
        if (searchAbortController !== null) {
          searchAbortController.abort('Existing request not needed');
          searchAbortController = null;
        }
        clearTimeout(searchTimeoutID);

        // Clear results
        this.classList.remove('main-search--has-results', 'main-search--results-on-multiple-lines', 'main-search--loading');
        resultsBox.innerHTML = '';
      }
    };

    searchInput.addEventListener('keyup', handleInputChange.bind(this));
    searchInput.addEventListener('change', handleInputChange.bind(this));
  }
};

window.customElements.define('main-search', MainSearch);
